<template>
  <div class="neg-comissoes">
    <div class="neg-painel">
      <negocios-campo-data
        label="Contratada a partir de"
        :value="negociosFiltrosComissao.dtini"
        @input="aplicar({ dtini: $event })"
      />

      <negocios-campo-data
        label="Contratada até"
        :value="negociosFiltrosComissao.dtfim"
        @input="aplicar({ dtfim: $event })"
      />

      <q-select
        dense
        clearable
        emit-value
        map-options
        label="Linha"
        option-value="id"
        option-label="nome"
        :options="negociosLinhas"
        :model-value="negociosFiltrosComissao.linha_id"
        @update:model-value="aplicar({ linha_id: $event })"
      />

      <q-select
        dense
        clearable
        emit-value
        map-options
        label="Etapa"
        option-value="id"
        option-label="nome"
        :options="negociosEtapas"
        :model-value="negociosFiltrosComissao.etapa_id"
        @update:model-value="aplicar({ etapa_id: $event })"
      />

      <q-select
        dense
        clearable
        emit-value
        map-options
        label="Operador"
        option-value="id"
        option-label="label"
        :options="negociosFuncionarios"
        :model-value="negociosFiltrosComissao.funcionario_id"
        @update:model-value="aplicar({ funcionario_id: $event })"
      />

      <q-toggle
        label="Somente o que falta receber"
        :model-value="negociosFiltrosComissao.pendentes"
        @update:model-value="aplicar({ pendentes: $event })"
      />
    </div>

    <div class="neg-totais">
      <div v-for="total in totais" :key="total.rotulo" class="neg-total">
        <div class="neg-total__rotulo">{{ total.rotulo }}</div>
        <div class="neg-total__valor" :class="total.classe">{{ total.valor }}</div>
      </div>
    </div>

    <q-table
      class="neg-tabela"
      row-key="oportunidade_id"
      dense
      flat
      selection="multiple"
      :rows="negociosItensComissao"
      :columns="colunas"
      :selected="selecionadas"
      @update:selected="atualizarSelecao"
      :loading="negociosCarregandoComissao"
      v-model:pagination="paginacao"
      no-data-label="Nenhuma proposta contratada no período"
      loading-label="Carregando comissões"
      rows-per-page-label="Por página"
    >
      <template v-slot:top>
        <div class="q-table__title">Comissões</div>
        <q-space />
        <q-btn
          flat
          no-caps
          icon="download"
          label="Baixar planilha"
          :loading="exportando"
          @click="exportar"
        />
        <q-btn
          unelevated
          no-caps
          color="primary"
          icon="payments"
          label="Dar baixa no repasse"
          :disable="!selecionadas.length"
          @click="$emit('receber', selecionadas)"
        />
      </template>

      <template v-slot:body-selection="props">
        <q-checkbox
          v-model="props.selected"
          color="secondary"
          :disable="props.row.pode_editar === false"
        />
      </template>

      <template v-slot:body-cell-diferenca="props">
        <q-td :props="props" class="text-right">
          <span :class="{ 'neg-negativo': props.row.diferenca < 0 }">
            {{ formatarMoeda(props.row.diferenca) }}
          </span>
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import negociosMixin from "@/mixins/negocios";

export default {
  name: "negocios-comissoes",

  emits: ["receber"],
  mixins: [negociosMixin],
  data() {
    return {
      selecionadas: [],
      exportando: false,
      paginacao: { rowsPerPage: 25 }
    };
  },
  computed: {
    ...mapGetters([
      "negociosItensComissao",
      "negociosTotaisComissao",
      "negociosFiltrosComissao",
      "negociosCarregandoComissao",
      "negociosLinhas",
      "negociosEtapas",
      "negociosFuncionarios"
    ]),
    totais() {
      const totais = this.negociosTotaisComissao;

      return [
        { rotulo: "Solicitado", valor: this.formatarMoeda(totais.solicitado), classe: "" },
        { rotulo: "Contratado", valor: this.formatarMoeda(totais.contratado), classe: "" },
        { rotulo: "Corte do banco", valor: this.formatarMoeda(totais.corte), classe: "" },
        { rotulo: "Previsto", valor: this.formatarMoeda(totais.previsto), classe: "" },
        { rotulo: "Recebido", valor: this.formatarMoeda(totais.recebido), classe: "" },
        {
          rotulo: "Diferença",
          valor: this.formatarMoeda(totais.diferenca),
          classe: totais.diferenca < 0 ? "neg-negativo" : ""
        }
      ];
    },
    colunas() {
      return [
        { name: "codigo", label: "Código", field: "codigo", align: "left", sortable: true },
        { name: "cliente", label: "Cliente", field: "cliente", align: "left", sortable: true },
        { name: "linha", label: "Linha", field: "linha", align: "left", sortable: true },
        { name: "operador", label: "Operador", field: "operador", align: "left", sortable: true },
        {
          name: "contratado_em",
          label: "Contratada em",
          field: "contratado_em",
          align: "left",
          sortable: true,
          format: data => this.dataParaTela(data)
        },
        {
          name: "valor_solicitado",
          label: "Solicitado",
          field: "valor_solicitado",
          align: "right",
          sortable: true,
          format: valor => (valor === null ? "—" : this.formatarMoeda(valor))
        },
        {
          name: "valor_contratado",
          label: "Contratado",
          field: "valor_contratado",
          align: "right",
          sortable: true,
          format: valor => this.formatarMoeda(valor)
        },
        {
          name: "corte",
          label: "Corte do banco",
          field: "corte",
          align: "right",
          sortable: true,
          format: valor => (valor === null ? "—" : this.formatarMoeda(valor))
        },
        {
          name: "percentual_previsto",
          label: "%",
          field: "percentual_previsto",
          align: "right",
          format: valor => this.formatarPercentual(valor)
        },
        {
          name: "comissao_prevista",
          label: "Previsto",
          field: "comissao_prevista",
          align: "right",
          sortable: true,
          format: valor => this.formatarMoeda(valor)
        },
        {
          name: "comissao_recebida",
          label: "Recebido",
          field: "comissao_recebida",
          align: "right",
          sortable: true,
          format: valor => this.formatarMoeda(valor)
        },
        {
          name: "diferenca",
          label: "Diferença",
          field: "diferenca",
          align: "right",
          sortable: true
        }
      ];
    }
  },
  methods: {
    aplicar(filtros) {
      this.selecionadas = [];
      this.$store.commit("negociosFiltrosComissao", filtros);
      this.$store.dispatch("carregarNegociosComissoes");
    },
    atualizarSelecao(selecionadas) {
      this.selecionadas = selecionadas.filter(item => item.pode_editar !== false);
    },
    exportar() {
      this.exportando = true;

      this.$store.dispatch("exportarNegociosComissoes").then(resposta => {
        this.exportando = false;

        this.avisar(resposta.message, resposta.success ? "green" : "orange");

        if (resposta.success && resposta.linkArquivo) {
          window.open(resposta.linkArquivo, "_blank");
        }
      });
    },
    limparSelecao() {
      this.selecionadas = [];
    }
  }
};
</script>
